// Сохранение файлов на устройство (экспорт записей и т.п.).
//
// Desktop / mobile web — классический Blob + <a download>; для удалённых URL —
// <a href> (backend выставляет Content-Disposition: attachment на presigned).
//
// Capacitor — НЕ <a download> (WebView игнорирует атрибут, файл «уходит в
// никуда») и НЕ dynamic import('@capacitor/*') (bare specifier не резолвится в
// production WebView — см. lib/open-auth-url.ts). Вместо этого canonical bridge
// window.Capacitor.Plugins: Filesystem пишет файл в Cache, Share открывает
// нативный share sheet («Сохранить в Файлы» / Save to device) — тот же UX,
// что у вложений в почте/мессенджерах. Fetch blob'а из WebView в S3 упирался
// бы в CORS Beget — нативный HTTP-клиент Filesystem.downloadFile его не
// подчиняется.

import { IS_CAPACITOR } from "@/lib/platform-detect";

interface FilesystemBridge {
	writeFile(opts: {
		path: string;
		data: string;
		directory: string;
		encoding?: string;
		recursive?: boolean;
	}): Promise<{ uri: string }>;
	getUri(opts: { path: string; directory: string }): Promise<{ uri: string }>;
	downloadFile(opts: {
		url: string;
		path: string;
		directory: string;
		recursive?: boolean;
	}): Promise<{ path: string }>;
}

interface ShareBridge {
	share(opts: { title?: string; text?: string; url?: string; dialogTitle?: string }): Promise<void>;
}

// Значения enum'ов @capacitor/filesystem (Directory.Cache / Encoding.UTF8) —
// стабильные строки нативного bridge; задаём литералами, чтобы не тянуть
// импорт плагина в web-бандл.
const DIR_CACHE = "CACHE";
const ENC_UTF8 = "utf8";
const EXPORT_DIR = "exports";

function getCapacitorBridges(): { fs: FilesystemBridge; share: ShareBridge } | null {
	if (typeof window === "undefined") return null;
	const plugins = (
		window as unknown as {
			Capacitor?: { Plugins?: { Filesystem?: FilesystemBridge; Share?: ShareBridge } };
		}
	).Capacitor?.Plugins;
	if (!plugins?.Filesystem || !plugins?.Share) return null;
	return { fs: plugins.Filesystem, share: plugins.Share };
}

/** Скачивание через скрытую ссылку (web). */
function anchorDownload(href: string, fileName: string): void {
	const a = document.createElement("a");
	a.href = href;
	a.download = fileName;
	a.rel = "noopener";
	document.body.appendChild(a);
	a.click();
	a.remove();
}

/**
 * Сохранить текстовый файл (транскрипт .txt / конспект .md).
 * Web — Blob-скачивание; Capacitor — Cache + нативный share sheet.
 */
export async function saveTextFile(opts: {
	fileName: string;
	text: string;
	mimeType: string;
}): Promise<void> {
	const bridges = IS_CAPACITOR ? getCapacitorBridges() : null;
	if (bridges) {
		const path = `${EXPORT_DIR}/${opts.fileName}`;
		await bridges.fs.writeFile({
			path,
			data: opts.text,
			directory: DIR_CACHE,
			encoding: ENC_UTF8,
			recursive: true,
		});
		const { uri } = await bridges.fs.getUri({ path, directory: DIR_CACHE });
		await bridges.share.share({ title: opts.fileName, url: uri });
		return;
	}
	const blob = new Blob([opts.text], { type: opts.mimeType });
	const url = URL.createObjectURL(blob);
	try {
		anchorDownload(url, opts.fileName);
	} finally {
		// Отложенный revoke — Safari может не успеть начать скачивание.
		setTimeout(() => URL.revokeObjectURL(url), 10_000);
	}
}

/** Blob → base64 без data-URL префикса (для Capacitor Filesystem binary write). */
function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result;
			if (typeof result !== "string") {
				reject(new Error("FileReader returned non-string"));
				return;
			}
			const comma = result.indexOf(",");
			resolve(comma >= 0 ? result.slice(comma + 1) : result);
		};
		reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
		reader.readAsDataURL(blob);
	});
}

/**
 * Сохранить бинарный Blob (PNG диаграммы и т.п.).
 * Web — object URL + <a download>; Capacitor — Cache (base64) + share sheet.
 */
export async function saveBlobFile(opts: { fileName: string; blob: Blob }): Promise<void> {
	const bridges = IS_CAPACITOR ? getCapacitorBridges() : null;
	if (bridges) {
		const path = `${EXPORT_DIR}/${opts.fileName}`;
		const data = await blobToBase64(opts.blob);
		// Без encoding → Filesystem трактует data как base64 бинарник.
		await bridges.fs.writeFile({
			path,
			data,
			directory: DIR_CACHE,
			recursive: true,
		});
		const { uri } = await bridges.fs.getUri({ path, directory: DIR_CACHE });
		await bridges.share.share({ title: opts.fileName, url: uri });
		return;
	}
	const url = URL.createObjectURL(opts.blob);
	try {
		anchorDownload(url, opts.fileName);
	} finally {
		setTimeout(() => URL.revokeObjectURL(url), 10_000);
	}
}

/**
 * Сохранить файл по удалённому URL (mp3 из S3, presigned + attachment).
 * Web — прямое скачивание по ссылке (download-атрибут cross-origin игнорируется,
 * но Content-Disposition: attachment на presigned решает); Capacitor — native
 * download в Cache + share sheet.
 */
export async function saveRemoteFile(opts: { url: string; fileName: string }): Promise<void> {
	const bridges = IS_CAPACITOR ? getCapacitorBridges() : null;
	if (bridges) {
		const path = `${EXPORT_DIR}/${opts.fileName}`;
		await bridges.fs.downloadFile({
			url: opts.url,
			path,
			directory: DIR_CACHE,
			recursive: true,
		});
		const { uri } = await bridges.fs.getUri({ path, directory: DIR_CACHE });
		await bridges.share.share({ title: opts.fileName, url: uri });
		return;
	}
	anchorDownload(opts.url, opts.fileName);
}

/** Санитизация имени файла из title записи (кросс-платформенные запреты FS). */
export function sanitizeFileName(title: string, fallback: string): string {
	const cleaned = title
		// biome-ignore lint/suspicious/noControlCharactersInRegex: целенаправленно вырезаем control-символы из имени файла
		.replace(/[\\/:*?"<>\u0000-\u001f]/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, 80);
	return cleaned.length > 0 ? cleaned : fallback;
}
